import type { Dict } from './index'

/**
 * English. Keys are the same as in de.ts; anything missing falls back to the
 * German text, so a half-finished translation never shows a placeholder.
 */
export const en: Partial<Dict> = {
  // ------------------------------------------------------------- General
  'app.name': 'UFS Atlas',
  'app.tagline': 'Ultimate Fishing Simulator 1',
  'app.searchPlaceholder': 'Search fish, bait, spot or method …',
  'app.loading': 'Loading …',
  'app.back': '← Back',
  'app.ok': 'Ok',
  'app.close': 'Close',
  'app.save': 'Save',
  'app.cancel': 'Cancel',
  'app.copy': 'Copy',
  'app.copied': '✓ Copied',
  'app.none': '–',
  'app.offline':
    'Accounts and groups need the server. Open the guide at {url} instead of as a local file.',

  // ---------------------------------------------------------- Navigation
  'nav.start': 'Start',
  'nav.fisheries': 'Fisheries',
  'nav.species': 'Species',
  'nav.baits': 'Baits',
  'nav.stats': 'Statistics',
  'nav.profile': 'Profile',
  'nav.login': 'Sign in',
  'nav.sources': 'Sources',
  'nav.caughtTotal': 'Species caught in total',
  'nav.toStart': 'To the start page',
  'nav.menu': 'Menu',
  'nav.openMenu': 'Open the menu',
  'nav.closeMenu': 'Close the menu',

  // ----------------------------------------------------------- Fisheries
  'map.overview': 'All fisheries',
  'map.maps': 'Maps',
  'map.spotsFromFiles': 'Spots per game files',
  'map.speciesHere': 'Species in this fishery',
  'map.style': 'Style',
  'map.caughtOf': '{done} of {total} caught',
  'map.travelPoints': '{n} travel points from the game files',
  'map.swarms': 'Fish shoals ({n})',
  'map.hoverHint': 'Hover a point for details, click to filter',
  'map.guideOnly': 'guide only',
  'map.dlcSpecies': 'DLC species',
  'map.fishHere': '{n} fish',
  'map.noProjection':
    'For this fishery the world coordinates of the shoals cannot be projected onto the map image reliably. Spot numbers and the species per spot still hold – only the extra shoal points stay hidden.',
  'map.entries': '{n} entries',
  'map.syncNote': 'State taken over from your account ({when}).',
  'map.profileTitle': 'Your profile – the address can be passed on',
  'map.readNote':
    'Spots, species lists, weight, length, bite times and the bait interests come straight from the game files – the percentages in the “From the game files” block are the game’s own numbers. Hook size, retrieve and depth above them are community experience.',
  'map.emptySpotNote':
    'Fish move. On an empty spot: use Hunter Vision or the fish finder, go down a hook size, or reload the map.',
  'map.guideOnlyNote':
    '“guide only” means: the game’s scene holds no spawn points for this species. That mostly affects the species of the New Fish Species DLC, which the game adds at runtime. The spot then comes from community research.',
  'map.announcedTitle': 'No solid guide data yet',
  'map.announcedText':
    'The Italy DLC is announced on Steam, but without a published date and without playable spot data.',
  'map.noHits': 'No hits. Clear the filters or use a shorter search term.',
  'map.hookAdviceTitle': 'Picking a hook, and empty spots',
  'map.hookAdvice':
    'The hook figures are community ranges and often aimed at big specimens. When the game says “hook too big”, start two to four steps smaller – the length and weight range in the game-data block is the best pointer for that. Go bigger only after you have landed the species at the spot. With natural bait three pieces widen the radius it draws from; with lures speed and gear ratio count together.',
  'map.filterCount': '{shown} of {total}',
  'map.onlySpecies': 'only {name}',
  'map.onlySpot': 'only spot {n}',
  'map.spread': 'spread freely',
  'map.morePanel': '+ {n} more species',
  'map.dlcToggle': 'New-Species-DLC',
  'map.footer':
    'UFS Atlas · guide data {guide} · game data {game} · a fan project, not affiliated with the developers.',
  'map.filter': 'Filter',
  'map.readHead': 'How to read these figures',
  'map.filterAll': 'All',
  'map.filterConfHigh': 'high confidence',
  'map.filterConfMedium': 'medium confidence',
  'map.filterConfLow': 'inferred values',
  'map.filterAllSpecies': 'all species',
  'map.filterOpen': 'only missing',
  'map.filterCaught': 'only caught',
  'map.favorites': 'Favourites',
  'map.compact': 'Compact',
  'map.spotList': 'Spot {list}',
  'map.groupBase': 'Base',
  'map.groupVariant': 'Variant',
  'map.groupDlc': 'DLC',
  'map.groupAnnounced': 'Announced',

  // Species that exist only in the game files: placeholders instead of guide text
  'gameOnly.spots': 'Spots {list}',
  'gameOnly.seeMap': 'see the map',
  'gameOnly.hook': 'match it to the size of the fish',
  'gameOnly.depth': 'check the shoal depth in the game',
  'gameOnly.method': 'not covered by the guide',
  'gameOnly.notes':
    'This species is in the game files of this fishery, but not yet in the researched guide.',
  'map.alt': 'Map of the fishery from the game files',
  'map.spotTitle': 'Spot {n}',
  'map.spotHead': 'SPOT {n}',
  'map.moreSpecies': '+ {n} more species',
  'map.noShoals': 'No shoals within casting range',
  'map.shoals': 'Fish shoals ({n})',
  'map.greenMarks': 'Marked green: spots holding {name}',
  'map.clearPick': 'Clear the selection',
  'map.spotPanelHead': 'Spot {n} · {count} species in range',
  'map.fishCount': '{n} fish',
  'map.atSpot': 'at the spot',
  'map.metres': '~{n} m',
  'map.noShoalsHere':
    'No shoal points within casting range – here you troll or fish from the boat.',
  'map.boatOnly':
    'The game files hold no map points for this fishery – here you fish from the boat only.',

  // ---------------------------------------------------------------- Fish
  'fish.spot': 'Spot',
  'fish.hook': 'Hook',
  'fish.bestTime': 'Best time',
  'fish.bestMethod': 'Best method',
  'fish.bestRetrieve': 'Best retrieve',
  'fish.baits': 'Baits',
  'fish.groundbait': 'Groundbait',
  'fish.fromGameFiles': 'From the game files',
  'fish.weight': 'Weight',
  'fish.length': 'Length',
  'fish.sizes': 'Matching size steps',
  'fish.weather': 'Weather',
  'fish.yourRecord': 'Your record',
  'fish.caught': 'caught',
  'fish.open': 'open',
  'fish.gameOnly': 'game data only',
  'fish.markCaught': 'Mark as caught',
  'fish.favorite': 'Favourite',
  'fish.spotN': 'Spot {n}',
  'fish.shoalPoints': 'Shoal points here',
  'fish.fishHere': 'Fish here',
  'fish.dlcSpecies': 'DLC species',
  'fish.dlcNote': '– no fixed spawn points in the scene',
  'fish.method': 'Method',
  'fish.baitInterest': 'Baits, interest per the prefab',
  'fish.retrieve': 'Retrieve when spin fishing',
  'fish.catches': 'Catches',
  'fish.recordFishery': 'Record fishery',
  'fish.practiceNote': 'From the water: ',
  'fish.sources': 'Sources: ',
  'fish.gameFiles': 'Game files',
  'fish.confidence': 'Confidence: {level}',
  'fish.confHigh': 'high',
  'fish.confMedium': 'medium',
  'fish.confLow': 'inferred',
  'fish.openSpecies': 'All about this species',

  // ------------------------------------------------------------- Methods
  'method.fly': 'Fly',
  'method.lure': 'Lure',
  'method.natural': 'Natural bait',
  'method.boilie': 'Boilie',
  'method.flyRod': 'Fly rod',
  'method.spinRod': 'Spinning rod',
  'method.floatGround': 'Float / ground',
  'method.groundRig': 'Ground rig',
  'method.threshold':
    'The game needs weather × preference × line ≥ 0.4 for a bite (casual 0.29).',
  'method.needsWeather': 'With {method} a weather value from {pct} % is enough.',
  'method.hopeless': 'No method gets past the threshold here without perfect conditions.',
  'method.threePieces': 'Three pieces on the hook lift natural bait to {pct} %.',

  // ---------------------------------------------------------- Statistics
  'stats.title': 'Statistics',
  'stats.level': 'Level',
  'stats.points': 'Points',
  'stats.species': 'Species',
  'stats.fisheriesComplete': 'Fisheries complete',
  'stats.catches': 'Catches',
  'stats.bites': 'Bites',
  'stats.totalWeight': 'Total weight',
  'stats.time': 'Time fished',
  'stats.heaviest': 'Heaviest',
  'stats.longest': 'Longest',
  'stats.loadSave': 'Load a save file',
  'stats.reset': 'Reset',
  'stats.resetAsk': 'Reset the local state including the ticks?',
  'stats.signedInAs': '◔ signed in as {name}',
  'stats.emptyTitle': 'No save file loaded yet',
  'stats.emptyText':
    'This page is filled entirely from your save file: catches per fishery, bites, time fished, points and your biggest fish per species. Nothing is uploaded, the file is only read in the browser.',
  'stats.emptyAccount':
    'With an account the state also lives on the server – then you can compare yourself in groups.',
  'stats.angler': 'Angler',
  'stats.xp': '{n} XP',
  'stats.money': 'Money',
  'stats.luckStrength': 'Luck {luck} % · Strength {strength} %',
  'stats.catchesTotal': 'Catches in total',
  'stats.bitesN': '{n} bites',
  'stats.caughtWeight': 'Weight caught',
  'stats.hitRate': '{pct} % hit rate',
  'stats.speciesCaught': '{n} species caught',
  'stats.tabFisheries': 'Fisheries',
  'stats.tabBiggest': 'Biggest fish',
  'stats.tabSets': 'Rod sets',
  'stats.noSets': 'The save file holds no stored rod sets.',
  'stats.setsHint':
    'The five sets from the game with rod, reel, line, float, hook, baits and rig. Hook step, depth and shot are the values each set was last set to.',
  'stats.ownedGear': 'Equipment bought',
  'stats.colFishery': 'Fishery',
  'stats.colFish': 'Fish',
  'stats.colRate': 'Rate',
  'stats.colTime': 'Time',
  'stats.colWeight': 'Weight',
  'stats.colBiggest': 'Biggest catch',
  'stats.sum': 'Total',
  'stats.colSpecies': 'Species',
  'stats.colYourRecord': 'Your record',
  'stats.colPossible': 'Possible',
  'stats.colShare': 'Share reached',
  'stats.colCatches': 'Catches',
  'stats.colTotal': 'Sum',
  'stats.colRecordFishery': 'Record fishery',

  // ------------------------------------------------------- Save-file import
  // --------------------------------------------- Save file and account
  'sync.aheadTitle': 'The newer state is here.',
  'sync.aheadText':
    'Your account holds an older save ({when}). It was not written over this one – you can upload the newer state.',
  'sync.aheadTextEmpty':
    'Your account holds no save yet. You can upload the one loaded here.',
  'sync.upload': 'Upload to my account',
  'sync.uploading': 'Uploading …',
  'sync.later': 'Later',
  'sync.uploadLead':
    'Send the file straight to the server – it is read there and replaces your profile. For a regular upload without clicking, the instructions are below.',
  'sync.pickFile': 'Upload a PROFILE file',
  'sync.uploadedN': 'Taken over: {n} species are now in your account.',
  'sync.title': 'Upload a save file',

  // Where the save files live, per system
  'paths.windows': 'Windows',
  'paths.mac': 'macOS',
  'paths.linux': 'Linux · Proton',
  'paths.copy': 'Copy the folder path',

  // Changelog
  'changelog.title': 'Changes',
  'legal.version': 'Version {v}',
  'changelog.lead':
    'What changed with each release. “General” holds everywhere, “Offline version” is about the download, “Server version” about the site with an account.',
  'changelog.general': 'General',
  'changelog.offline': 'Offline version',
  'changelog.server': 'Server version',

  // Tutorial: uploading by itself
  'howto.title': 'Upload by itself',
  'howto.lead':
    'One command, run every two hours – your profile then always matches your last session. The token is part of the command, so keep it to yourself.',
  'howto.winNote':
    'Task scheduler: run the command in a command prompt as administrator. It creates the task “UFS Atlas Upload”. Windows ships curl itself since version 1803.',
  'howto.macNote':
    'cron: open “crontab -e” in the terminal and add the line. On the first run macOS asks for access to the folder – allow it once.',
  'howto.linuxNote':
    'cron: open “crontab -e” and add the line. The path points into the Proton prefix of app 468920; with a Steam library on another disk it sits under that disk’s steamapps/compatdata.',
  'howto.tail':
    'Every upload replaces your profile entirely. If the job runs while the game is saving, the next run catches up – nothing is lost.',

  'import.title': 'Read a save file',
  'import.lead':
    'The guide reads your save file and ticks off every species you have already caught. The file stays in the browser and is sent nowhere.',
  'import.whereLead': 'You find it here:',
  'import.slots':
    'PROFILE_0, PROFILE_1 and PROFILE_2 are the three profile slots of the game – take the one you play in. The files deliberately have no extension.',
  'import.reading': 'Reading …',
  'import.pick': 'Choose a PROFILE file',
  'import.noCounters':
    'No catch counters found. Please choose a PROFILE file: PROFILE_0, PROFILE_1 or PROFILE_2, each without an extension.',
  'import.done': '{who}{n} species caught taken over, personal records included.',
  'import.alsoServer': 'Stored in your account as well.',
  'import.serverFailed': '(upload to the server failed: {error})',
  'import.unreadable': 'The file could not be read: {error}',
  'import.unreadablePlain': 'The file could not be read.',
  'import.byHand': 'You can also tick species by hand – the state is kept in the browser.',
  'import.resetAsk': 'Really remove every tick and all imported records?',
  'import.reset': 'Reset the catch list',

  // ---------------------------------------------------------------- Sources
  'sources.eyebrow': 'What this is built on',
  'sources.title': 'Sources & data quality',
  'source.official': 'Official',
  'source.wiki': 'Community wiki',
  'source.test': 'Community test',
  'source.guide': 'Community guide',
  'source.secondary': 'Secondary source',
  'source.observation': 'Community observation',
  'sources.note':
    'Spots, species lists, weight and length ranges, bite curves and the bait interests come straight from the installed game files (Unity scenes, fish and bait prefabs, localisation table). Hook and retrieve recommendations stay community experience; anything rated low is marked as a starting point on purpose.',

  // ----------------------------------------------------------- All fisheries
  'overview.caught': 'Species caught',
  'overview.caughtSub': '{pct} % of the species list',
  'overview.completeSub': 'every species of the fishery caught',
  'overview.left': 'Still open',
  'overview.leftSub': 'species without a tick',
  'overview.perFishery': 'Progress per fishery',
  'overview.colCaught': 'Caught',
  'overview.colProgress': 'Progress',
  'overview.colOpen': 'Open',

  // -------------------------------------------------------------- Account
  'auth.title': 'Sign in',
  'auth.intro':
    'No password: you get a six-digit code by e-mail. With an account your profile lives on the server, you can join groups and compare.',
  'auth.email': 'your@mail.com',
  'auth.requestCode': 'Request code',
  'auth.sending': 'Sending …',
  'auth.checking': 'Checking …',
  'auth.codeSent': 'The code is on its way. It is valid for 15 minutes.',
  'auth.verify': 'Sign in',
  'auth.verifying': 'Checking …',
  'auth.stayLoggedIn': 'Stay signed in',
  'auth.stayHint': '(90 days, on this device only)',
  'auth.logout': 'Sign out',
  'auth.codePlaceholder': '123456',
  'auth.altchaOk': 'Not a bot – checked',
  'auth.altchaWork': 'Checking … {pct} %',
  'auth.altchaPrepare': 'Preparing the check …',
  'auth.altchaBy': 'ALTCHA · own server',
  'auth.altchaNoCrypto': 'This browser offers no cryptography functions.',
  'auth.altchaFailed': 'The bot check did not work out. Please reload the page.',

  // --------------------------------------------------------------- Profile
  'profile.yours': 'Your profile',
  'profile.other': 'Profile',
  'profile.overview': 'Overview',
  'profile.records': 'Species',
  'profile.missing': 'Still missing',
  'profile.duel': 'Compared to you',
  'profile.groups': 'Groups',
  'profile.followers': 'Followers',
  'profile.follows': 'Following',
  'profile.settings': 'Settings',
  'profile.copyLink': 'Copy link',
  'profile.follow': 'Follow',
  'profile.following': '✓ Following',
  'profile.saveState': 'Save file from',

  'profile.copyPrompt': 'Address to copy:',
  'profile.noSaveYet': 'no save file',
  'profile.nComplete': '{n} complete',
  'profile.nRecords': '{n} records',
  'profile.nSpecies': '{n} species',
  'profile.nPieces': '{n}',
  'profile.settingsSub': 'Name, token, sign out',
  'profile.anglerLine': 'Angler {name}',
  'profile.noPublicGroup': 'This angler is in no public group.',
  'profile.yourGroups': 'Your groups',
  'profile.duelSignIn': 'Sign in and your profile stands here against {name}, value for value.',
  'profile.duelNoSave':
    'The comparison needs your own save file. Upload it under “Settings”.',
  'profile.needsServer': 'Profiles need the server. Open the guide at {url}.',

  // Profile, overview and tables
  'profile.sortSum': 'Total weight',
  'profile.sortBest': 'Best weight',
  'profile.sortLength': 'Length',
  'profile.sortCount': 'Count',
  'profile.sortName': 'Name',
  'profile.experience': '{n} experience',
  'profile.skills': 'Skills',
  // The skill tree from the save file
  'skills.title': 'Skills',
  'skills.unlocked': '{done} of {total} unlocked',
  'skills.points': '{n} points to spend',
  'skills.step': 'step {level} of {steps}',
  'skills.have': 'unlocked',
  'skills.open': 'open',

  // Legal
  'legal.privacy': 'Privacy',
  'legal.imprint': 'Imprint',
  'legal.github': 'Source on GitHub',
  'cookie.text':
    'This site sets two cookies, and only once you sign in: one for the session, one for staying signed in. No analytics, no advertising, no outside services.',
  'cookie.ok': 'Understood',

  'privacy.title': 'Privacy policy',
  'privacy.lead':
    'The guide is a fan project. It gets by on as little data as it can, and what it does store is all written down here.',

  'privacy.responsibleTitle': 'Who is responsible',
  'privacy.responsibleText':
    'The operator of this site is responsible for it; the address is in the imprint.',

  'privacy.shortTitle': 'The short version',
  'privacy.shortText':
    'Without an account not one of your figures leaves the browser. With an account your e-mail address, your display name and the numbers from the save file you uploaded live on the server. There are no analytics tools, no advertising and no embedded third-party services.',

  'privacy.localTitle': 'What stays in the browser',
  'privacy.localText':
    'Ticked species, favourites, the chosen language and the figures of a loaded save file sit in your browser’s local storage. They are not transmitted. Clear the site data and they are gone.',
  'privacy.localSave':
    'The game’s PROFILE file is read in the browser. It is only uploaded if you are signed in and start the upload yourself.',

  'privacy.accountTitle': 'Account',
  'privacy.accountWhat':
    'An account holds: your e-mail address, a display name, an access token for uploading without signing in, when you last signed in, and – once you push a save file – the figures read from it: catches, records, time fished, progress per fishery, equipment, rod sets and skills.',
  'privacy.accountWhy':
    'The basis is performing the service you asked for (Art. 6(1)(b) GDPR). Without an e-mail address there is no way to send a sign-in code.',
  'privacy.accountKeep':
    'The data stays until you delete the account. Write to the address below and the account and its profile are removed.',

  'privacy.publicTitle': 'What others can see',
  'privacy.publicText':
    'Your profile is reachable at its address once you pass it on: display name, the figures from your save file, the gear you bought, your rod sets, the skills you unlocked and your public groups. Your e-mail address is never shown. Inside a group the members see your name and your figures.',

  'privacy.cookiesTitle': 'Cookies',
  'privacy.cookiesText':
    'Two of them, both only after signing in: the session cookie (PHPSESSID, 30 days) and ufs_remember for staying signed in (90 days). Both are httpOnly and SameSite=Lax. They are needed for signing in to work at all, so no consent is required. Without an account the site sets no cookie.',

  'privacy.logsTitle': 'Server logs',
  'privacy.logsText':
    'The web server logs requests as web servers do: IP address, time, the address requested and the browser string. That serves running the site and fending off abuse (Art. 6(1)(f) GDPR).',

  'privacy.mailTitle': 'E-mail',
  'privacy.mailText':
    'Sign-in codes go out through an e-mail provider. Your address and the code are passed to it. Which provider that is stands in the imprint.',

  'privacy.thirdTitle': 'No third-party services',
  'privacy.thirdText':
    'No fonts, scripts or images are loaded from anyone else’s server. The bot check on sign-in (ALTCHA) computes in the browser and is verified by this server – no captcha provider involved.',

  'privacy.rightsTitle': 'Your rights',
  'privacy.rightsText':
    'You can ask for access, correction, deletion, restriction and portability, and you can object to processing. You are also free to complain to a data protection authority.',

  'privacy.contactTitle': 'Contact',
  'privacy.contactMissing': 'The contact address is in the imprint.',
  'profile.luck': 'Luck {n}',
  'profile.strength': 'Strength {n}',
  'profile.topSpecies': 'Strongest species',
  'profile.avgPerCatch': 'avg {n} kg per catch',
  'profile.catchesPerHourSub': '{n} catches per hour',
  'profile.colWeight': 'Weight',
  'profile.colBestLength': 'Best length',
  'profile.colAvgPerPiece': 'avg each',
  'profile.colRecordFrom': 'Record from',
  'profile.colDifference': 'Difference',
  'profile.colCommonSpecies': 'Species both have',
  'profile.colDraw': 'draw',
  'profile.colLead': 'Lead',
  'profile.fisheryNote':
    'Weight in kg. “Species” counts the fishery’s species list; the figures beside it come from the fishery statistics in the save file.',
  'profile.speciesNote': 'Best and total weight in kg, length in cm.',
  'profile.onlyThem': 'Only {name} has these',
  'profile.onlyYou': 'Only you have these',
  'profile.speciesBalance': 'Species balance',
  'profile.bothHave': 'Both: {n}',
  'profile.onlyName': 'Only {name}: {n}',
  'profile.onlyMe': 'Only you: {n}',
  'profile.noneYet': 'Neither yet: {n}',
  'profile.tie': 'None – you are level.',
  'profile.loading': 'Loading …',
  'profile.followerCount': 'Followers',
  'profile.followsCount': 'Follows',
  'profile.saveVersion': ' · save file {version}',
  'profile.menuFollow': '{followers} follow · follows {follows}',
  'profile.noSaveOther': 'This angler has not uploaded a save file yet.',
  'profile.membersOf': '{n} members',
  'profile.youAreAdmin': ' · you are the admin',
  'profile.byOwner': ' · by {name}',
  'profile.shareOfList': '{pct} % of the species list',
  'profile.visitedFisheries': '{n} fisheries visited',
  'profile.progressPerFishery': 'Progress per fishery',
  'profile.recordsPerSpecies': 'Records per species',
  'profile.noSpeciesYet': 'No species caught yet.',
  'profile.nOpen': '({n} open)',
  'duel.figures': 'Figures',
  'duel.anglerLine': 'Angler {name} · level {level}',
  'duel.stateLine': 'From: {when} ({ago})',
  'duel.heavierFish': 'Heavier fish',
  'duel.onlyMe': 'Only you: {n}',
  'duel.neitherYet': 'Neither yet: {n}',
  'duel.theyMissIt': 'they are missing it',
  'duel.youMissIt': 'you are missing it',
  'duel.noSpeciesInPick': 'No species in this selection.',
  'duel.speciesCount': '{shown} of {total} species at least one of you has caught. ',

  // Account settings
  'account.nameSaved': 'Name saved.',
  'account.nameHint':
    'This is the name others find you under in the search and in groups. It is unique – if someone has it already, you have to pick another one.',
  'account.userName': 'User name',
  'account.title': 'Account',
  'account.signedInLabel': 'Signed in as',
  'account.namePlaceholder': 'your name',
  'account.shareTitle': 'Share your profile',
  'account.autoUpload': 'Upload a save file automatically',
  'account.localWithSave': '– plus the figures of the save file loaded last.',
  'account.saveName': 'Save the name',
  'account.linkHint':
    'This address shows your profile. Anyone signed in who opens it sees their own state beside yours – figure for figure, fishery for fishery, species for species.',
  'account.addressCopied': 'Address copied.',
  'account.viewProfile': 'View the profile',
  'account.pushAsk': 'The local state replaces your profile in the account entirely. Go ahead?',
  'account.pushed': 'Taken over: {n} species in the account.',
  'account.push': 'Push the local state to the account',
  'account.localHas':
    '{n} species are ticked in this browser. That is enough to fill the profile in the account without loading the PROFILE file again.',
  'account.localEmpty':
    'Nothing is stored in this browser. Tick species under “Fisheries” or load a save file under “Statistics”.',
  'account.tokenHint':
    'With this command you upload your save file without signing in – from the Windows task scheduler after every session, for one. Every upload replaces your profile entirely.',
  'account.slotLabel': 'Profile slot:',
  'account.uploadDone': 'Save file uploaded and taken into your profile.',
  'account.copyCommand': 'Copy the command',
  'account.newToken': 'Renew the token',
  'account.newTokenAsk': 'Create a new token? The old one stops working then.',
  'account.signedInAs': 'Signed in as {name}',
  'account.followersOf': 'Anglers following this profile',
  'account.followsOf': 'Profiles this account follows',
  'account.manageGroups': 'Manage groups',
  'account.notInGroup': 'You are in no group yet.',

  // Comparing two profiles
  'duel.progress': 'Progress',
  'duel.yield': 'Yield',
  'duel.records': 'Records',
  'duel.efficiency': 'Efficiency',
  'duel.speciesCaught': 'Species caught',
  'duel.fisheriesVisited': 'Fisheries visited',
  'duel.atLeastOneBite': 'at least one bite',
  'duel.heaviestFish': 'Heaviest fish',
  'duel.longestFish': 'Longest fish',
  'duel.weightOneSpecies': 'Weight of one species',
  'duel.sumStrongest': 'sum of the heaviest species',
  'duel.bitesUsed': 'Bites turned into fish',
  'duel.catchesPerBite': 'catches per bite',
  'duel.catchesPerHour': 'Catches per hour',
  'duel.weightPerCatch': 'Weight per catch',
  'duel.pointsPerHour': 'Points per hour',
  'duel.level': 'level',
  'duel.youLead': '▲ you',
  'duel.theyLead': '▼ they',
  'duel.longerFish': 'Longer fish',
  'duel.morePieces': 'More of them',
  'duel.fisheryByFishery': 'Fishery by fishery',
  'duel.speciesBySpecies': 'Species by species',
  'duel.filterAll': 'All',
  'duel.filterDiff': 'Differences',
  'duel.filterBoth': 'Both caught',
  'duel.filterHis': 'Only them',
  'duel.filterMine': 'Only me',
  'duel.filterLead': 'I lead',
  'duel.filterBehind': 'They lead',
  'profile.noSave': 'No save file uploaded yet.',

  // ------------------------------------------------------------ Start page
  'start.eyebrow': 'Fan atlas · Ultimate Fishing Simulator 1',
  'start.headline': 'The numbers come from the game files.',
  'start.lead':
    'Spots, species lists, bite times, bait preferences and size steps are read out, not guessed. Anything from community research says so.',
  'start.ctaMaps': 'Open the fisheries',
  'start.ctaSpecies': 'Browse species',
  'start.ctaSave': 'Load a save file',
  'start.wallTitle': 'Bite readiness over 24 hours, per species from the game',
  'start.wallHint': 'dark = night · click to open',

  'start.factFisheries': 'Fisheries',
  'start.factSpecies': 'Species',
  'start.factSpots': 'Spots',
  'start.factShoals': 'Shoal points',
  'start.factBaits': 'Baits',
  'start.factCurves': 'Bite curves',

  'start.featuresTitle': 'What the atlas does',
  'start.mapsTitle': 'Fisheries and spots',
  'start.mapsText':
    '{maps} maps with the spot numbers from the game, the shoal points on the map image and the species per spot — including how many fish stand there.',
  'start.mapsCta': 'Open the maps',
  'start.speciesTitle': 'Species',
  'start.speciesText':
    '{species} species with weight and length range, bite curve, weather curves and the description from the game encyclopedia.',
  'start.speciesCta': 'Species list',
  'start.baitsTitle': 'Baits and methods',
  'start.baitsText':
    'For every species the bait preference in percent, the best method — fly, lure, float or ground — and the factor of every retrieve when spin fishing.',
  'start.baitsCta': 'Bait page',
  'start.sizesTitle': 'Size steps',
  'start.sizesText':
    'Hooks from #12 to #12/0 with the weight range they catch, plus the steps for lures and flies. {steps} steps, straight from the FishManager.',
  'start.sizesCta': 'To the species',
  'start.saveTitle': 'Your save file',
  'start.saveText':
    'Load the PROFILE file and see what is missing: per fishery, per species, your record next to what is possible. The file stays in the browser.',
  'start.saveCta': 'Load a save file',
  'start.profileTitle': 'Profile and comparison',
  'start.profileText':
    'An account gives you a shareable profile page. Anyone signed in who opens it sees their own state next to yours — figure by figure, fishery by fishery, species by species.',
  'start.profileCta': 'Create an account',
  'start.groupsTitle': 'Groups',
  'start.groupsText':
    'Public, unlisted or private. Eight boards: heaviest and longest fish, total weight, strongest species, species count, complete fisheries, catches, time fished.',
  'start.groupsCta': 'See the groups',
  'start.offlineTitle': 'Works without a server',
  'start.offlineText':
    'The guide is plain HTML and JavaScript. Offline everything but accounts and groups keeps working; ticked species live in the browser.',
  'start.offlineCta': 'Try it',

  'start.sourcesTitle': 'Where the numbers come from',
  'start.fromGame': 'From the game files',
  'start.fromGame1': 'Spot numbers and their place on the map image',
  'start.fromGame2': 'Species per fishery and per spot, with fish counts',
  'start.fromGame3': 'Weight and length range, bite curve, weather curves',
  'start.fromGame4': 'Bait preference per species, bait type, retrieve factors',
  'start.fromGame5': 'Size steps for hooks, lures, flies and baits',
  'start.fromGame6': 'Names and descriptions from the localisation table',
  'start.fromCommunity': 'From community research',
  'start.fromCommunity1': 'Hook sizes as experience per species and fishery',
  'start.fromCommunity2': 'Depth hints and rig notes',
  'start.fromCommunity3': 'Strategies per spot and a confidence level per entry',
  'start.communityNote':
    'These values are marked as community data in the guide. Wherever the game files hold something more precise, it wins.',

  'start.openTitle': 'Open and verifiable',
  'start.openText':
    'The source and the tools that read the game files are open. Anyone who doubts a number can reproduce it from their own installation.',
  'start.openCta': 'View on GitHub',
  'start.footer': 'Fan project, not affiliated with the developers. Guide data {guide}.',

  // ---------------------------------------------------------------- Groups
  'group.public': 'Public',
  'group.publicHint': 'listed in the directory, anyone may join',
  'group.unlisted': 'Unlisted',
  'group.unlistedHint': 'only found by link or code, joining is open',
  'group.private': 'Private',
  'group.privateHint': 'only members see it, joining needs the code',
  'group.create': 'New group',
  'group.join': 'Join',
  'group.joinWithCode': 'Join with code',
  'group.leave': 'Leave',
  'group.edit': 'Edit',
  'group.delete': 'Delete group',
  'group.members': 'Members',
  'group.admin': 'Group admin',
  'group.newCode': 'New code',
  'group.directory': 'Public groups',
  'group.title': 'Groups',
  'group.yours': 'Your groups',
  'group.count': '{n} total',
  'group.memberCount': '{n} members',
  'group.youAreAdmin': 'you are admin',
  'group.byOwner': 'by {name}',
  'group.needsAccount': 'Groups come with an account.',
  'group.needsServer': 'Groups need the server. Open the guide at {url}.',
  'group.noneYet': 'You are not in a group yet. Create one or join a public one.',
  'group.noneOther': 'This angler is not in any public group.',
  'group.open': 'Open',
  'group.allGroups': '← All groups',
  'group.namePlaceholder': 'Name of the group',
  'group.createHint': 'Whoever creates a group is its admin — if they leave, the group is dissolved.',
  'group.codePlaceholder': 'ABC123',
  'group.search': 'search …',
  'group.searchGo': 'Search',
  'group.noneFound': 'No public group found.',
  'group.alreadyIn': '✓ joined',
  'group.manage': 'Manage groups',
  'group.editTitle': 'Edit group',
  'group.newCodeHint': 'The old code stops working afterwards',
  'group.confirmLeave': 'Really leave the group "{name}"?',
  'group.confirmLeaveAdmin':
    'You are the admin of "{name}". Leaving dissolves the group. Continue?',
  'group.confirmDelete': 'Dissolve the group "{name}" for good?',
  'group.confirmKick': 'Remove {name} from the group?',
  'group.kick': 'Remove',
  'group.code': 'Code {code}',
  'group.noData': 'No data yet',
  'group.done': 'Done',

  // Group boards
  'board.biggestFish': 'Heaviest fish',
  'board.longestFish': 'Longest fish',
  'board.totalWeight': 'Most weight overall',
  'board.topSpeciesWeight': 'Most weight of one species',
  'board.species': 'Most species',
  'board.fisheriesComplete': 'Complete fisheries',
  'board.fish': 'Most catches',
  'board.time': 'Most time fished',

  // Member table columns
  'col.angler': 'Angler',
  'col.species': 'Species',
  'col.fisheriesComplete': 'Fisheries complete',
  'col.catches': 'Catches',
  'col.weight': 'Weight',
  'col.heaviest': 'Heaviest',
  'col.state': 'Save from',
  'col.noProfile': 'no profile',

  // Retrieve when spin fishing, in the order of the SpinningMethod enum
  'spin.straightSlow': 'Straight slow',
  'spin.straight': 'Straight',
  'spin.straightFast': 'Straight fast',
  'spin.liftDrop': 'Lift & drop',
  'spin.stopGo': 'Stop & go',
  'spin.twitching': 'Twitching',

  // Weather dials
  'weather.wind': 'Wind',
  'weather.windHint': 'calm → stormy',
  'weather.clouds': 'Cloud cover',
  'weather.cloudsHint': 'clear → overcast',
  'weather.rain': 'Rain',
  'weather.rainHint': 'dry → steady rain',

  'method.noChance': 'No chance: {list}.',

  // Species page
  'species.searchPlaceholder': 'Search species …',
  'species.onlyMissing': 'only missing',
  'species.count': '{n} species',
  'species.noSize': 'no size given',
  'species.fisheries': 'Fisheries',
  'species.noSpawns': 'no spawn points',
  'species.backToList': 'Back to the species list',
  'species.fromGuide': 'From community research',
  'species.unknown': 'The game files hold no such species.',
  'bait.backToList': 'Back to the bait list',
  'bait.interestTitle': 'Interest per species',
  'bait.openSpecies': 'Open the species',
  'bait.noSpecies': 'No species is recorded for this bait.',
  'bait.unknown': 'The game files hold no such bait.',
  'species.method': 'Method',
  'species.retrieve': 'Retrieve',

  'fish.allDay': 'the same around the clock',
  'fish.hours24': '24 h',
  'fish.showLess': 'fewer',
  'fish.showAllBaits': 'all {n} baits',
  'bait.baitSize': 'Bait size',

  // -------------------------------------------------------------- Bait page
  'bait.intro':
    'Every bait prefab in the game carries a list of how strongly each species wants it — a value between 0 and 100 %. Those tables are shown here unchanged. They say nothing about hook size or retrieve; that stays experience.',
  'bait.searchPlaceholder': 'Search bait or fish …',
  'bait.onlyFor': 'only for {name}',
  'bait.countOf': '{shown} of {total} baits',
  'bait.speciesCount': '{n} species',
  'bait.strongest': 'strongest: {list}',
  // Lure type, in the game's own words
  'baitType.spinner': 'Spinner',
  'baitType.spoon': 'Spoon',
  'baitType.wobbler': 'Hard lure',
  'baitType.softBait': 'Soft lure',
  'baitType.fly': 'Fly',
  'baitType.flyDry': 'Dry fly',
  'baitType.flyWet': 'Wet fly',
  'baitType.flyNymph': 'Nymph',
  'baitType.flyStreamer': 'Streamer',
  'bait.noTableShort': 'no values in the game files',
  'bait.noTableNote':
    'The game files hold no interest values for this bait – all 154 entries are 0. The game evidently works the interest out from the fish the cut came off.',
  'bait.naturalNote': 'Offered on the hook; more pieces widen the attraction radius.',
  'bait.boilieNote': 'For carp and big fish, on a hair rig.',
  'bait.flyNote': 'Four types, fished at different depths.',
  'bait.lureNote': 'Retrieved actively; the retrieve has a say in the bite.',
  'bait.hookSizes': 'Hook sizes',
  'bait.hookText':
    'Each size lists the fish weight it catches; the range moves up with the size. A hook that is too big misses small fish — hence the in-game hint to try a smaller one. Lures start at #8, flies at #4.',
  'bait.colSize': 'Size',
  'bait.colGap': 'Gap',
  'bait.colHook': 'Hook · fish weight',
  'bait.colBaitSize': 'Bait size · fish length',
  'bait.biteTitle': 'What else a bite needs',
  'bait.biteText':
    'Besides the bait the game weighs nine dials, each with its own curve per species: time of day, hunger, bait depth, temperature, wind, pressure, cloudiness, rain and bait speed. Only four are filled in — time, wind, cloudiness and rain. The other five sit at a constant 1 for every species and do nothing.',
  'bait.depthLead': 'Bait depth in particular:',
  'bait.depthText':
    'it does not decide whether a fish bites, only whether the bait reaches where the shoal stands. Depth values in guides are observations about where the fish are, not a preference.',

  // Parts of a rod set, as the save file lists them
  'slot.rod': 'Rod',
  'slot.iceRod': 'Ice rod',
  'slot.reel': 'Reel',
  'slot.line': 'Line',
  'slot.float': 'Float',
  'slot.hook': 'Hook',
  'slot.boilie': 'Boilie',
  'slot.feeder': 'Feeder',
  'slot.feederBait': 'Feeder bait',
  'slot.rodStand': 'Rod stand',
  'slot.biteIndicator': 'Bite indicator',

  // Follows
  'follows.followYou': 'Follow you',
  'follows.followThem': 'Follow {name}',
  'follows.youFollow': 'You follow',
  'follows.theyFollow': '{name} follows',
  'follows.noneYou': 'Nobody follows you yet.',
  'follows.noneOther': 'Nobody follows this profile yet.',
  'follows.youNone': 'You do not follow anyone yet.',
  'follows.otherNone': 'This profile does not follow anyone yet.',
  'follows.species': '{n} species',
  'follows.catches': '{n} catches',

  // Glossary: headings for the bait groups. The entries themselves come from the
  // game files; the guide names the groups.
  'gloss.natural.title': 'Natural baits',
  'gloss.natural.note': 'Offered on the hook; several pieces widen the radius they draw from.',
  'gloss.boilie.title': 'Boilies',
  'gloss.boilie.note': 'For carp and big fish, on a hair rig.',
  'gloss.lure.title': 'Lure types',
  'gloss.lure.note': 'Worked actively; the retrieve has its own say in the bite.',
  'gloss.method.title': 'Fishing methods',
  'gloss.method.note': 'The five spinning retrieves come at three speeds each.',
  'gloss.gear.title': 'Rig & equipment',
  'gloss.gear.note': '',

  'gear.set': 'Set {n}',
  'gear.hookStep': 'Hook step',
  'gear.depth': 'Depth',
  'gear.shot': 'Shot',

  // Equipment classes from the save file
  'cat.ROD': 'Rods',
  'cat.ICE_ROD': 'Ice rods',
  'cat.ROD_STAND': 'Rod stands',
  'cat.REEL': 'Reels',
  'cat.LINE': 'Lines',
  'cat.FLOAT': 'Floats',
  'cat.HOOK': 'Hooks',
  'cat.BAIT': 'Natural baits',
  'cat.BOILIE': 'Boilies',
  'cat.FEEDER': 'Feeders',
  'cat.FEEDER_BAIT': 'Feeder bait',
  'cat.BITE_INDICATOR': 'Bite indicators',
  'cat.LURE': 'Lures',
  'cat.SPOON': 'Spoons',
  'cat.SPINNER': 'Spinners',
  'cat.WOBBLER': 'Wobblers',
  'cat.SOFT': 'Soft baits',
  'cat.FLY': 'Flies',
  'cat.BOAT': 'Boats',
  'cat.DRILLER': 'Augers',
  'cat.FISHING': 'Landing nets',
  'cat.GAS': 'Gas stoves',
  'cat.BEER': 'Drinks',
  'cat.FILLET': 'Fillets',
  'cat.ICE': 'Ice gear',
}
